import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { Queries } from 'api/queries.enum'
import { Mutations } from 'api/mutations.enum'
import {
    GetPulseSurveyDetailResponse,
    GetPulseSurveysResponse,
    UpdatePulseSurveyPayload,
    UpdatePulseSurveyResponse,
} from 'api/actions/pulseSurveys/pulseSurveysActions.types'
import { updatePulseSurvey } from 'api/actions/pulseSurveys/pulseSurveysActions'
import { usePulseSurveys } from 'hooks/usePulseSurveys'
import { ACTION_ALLOWED_STATUTES } from 'containers/PulseSurveys/pulseSurvey.helpers'

export const useUpdatePulseSurvey = () => {
    const queryClient = useQueryClient()
    const { activePulseSurvey, onChangeActivePulseSurvey } = usePulseSurveys()

    const mutation = useMutation<
        UpdatePulseSurveyResponse,
        AxiosError,
        UpdatePulseSurveyPayload,
        {
            previousPulseSurveyDetail: GetPulseSurveyDetailResponse | undefined
            previousPulseSurveys: GetPulseSurveysResponse | undefined
            previousActivePulseSurvey: typeof activePulseSurvey | undefined
        }
    >({
        mutationFn: updatePulseSurvey,
        mutationKey: [Mutations.updatePulseSurvey],
        onMutate: async (variables) => {
            await queryClient.cancelQueries([Queries.getPulseSurveyDetail, { surveyUUID: variables.surveyUUID }])
            await queryClient.cancelQueries([Queries.getPulseSurveys])

            const previousPulseSurveyDetail = queryClient.getQueryData<GetPulseSurveyDetailResponse>([
                Queries.getPulseSurveyDetail,
                { surveyUUID: variables.surveyUUID },
            ])

            const previousPulseSurveys = queryClient.getQueryData<GetPulseSurveysResponse>([Queries.getPulseSurveys])

            const previousActivePulseSurvey = activePulseSurvey

            queryClient.setQueryData<GetPulseSurveyDetailResponse>(
                [Queries.getPulseSurveyDetail, { surveyUUID: variables.surveyUUID }],
                (old) => {
                    if (!old) {
                        return old
                    }

                    if (ACTION_ALLOWED_STATUTES.update.includes(old.status)) {
                        return {
                            ...old,
                            ...variables.body,
                        }
                    }

                    return {
                        ...old,
                        ...variables.body,
                        title: old.title,
                        ...(variables.body.config_data && {
                            config_data: {
                                ...variables.body.config_data,
                                title: old.title,
                                display_name: variables.body.title || old.title,
                            },
                        }),
                    }
                },
            )

            queryClient.setQueryData<GetPulseSurveysResponse>([Queries.getPulseSurveys], (old) => {
                if (!old) {
                    return old
                }

                return old.map((survey) => {
                    if (survey.uuid !== variables.surveyUUID) {
                        return survey
                    }

                    return {
                        ...survey,
                        ...variables.body,
                        title: survey.title,
                        display_name: variables.body.title || survey.title,
                    }
                })
            })

            const title = variables.body.title

            if (title && activePulseSurvey?.uuid === variables.surveyUUID) {
                onChangeActivePulseSurvey({
                    ...activePulseSurvey,
                    display_name: title,
                    title,
                })
            }

            return { previousPulseSurveyDetail, previousPulseSurveys, previousActivePulseSurvey }
        },
        onError: (_error, variables, context) => {
            queryClient.setQueryData(
                [Queries.getPulseSurveyDetail, { surveyUUID: variables.surveyUUID }],
                context?.previousPulseSurveyDetail,
            )

            queryClient.setQueryData([Queries.getPulseSurveys], context?.previousPulseSurveys)

            if (context?.previousActivePulseSurvey) {
                onChangeActivePulseSurvey(context.previousActivePulseSurvey)
            }
        },
        onSettled: async (_data, _error, variables) => {
            await queryClient.invalidateQueries([Queries.getPulseSurveyDetail, { surveyUUID: variables.surveyUUID }])

            await queryClient.invalidateQueries([Queries.getPulseSurveys])
        },
    })

    return mutation
}
